import React, { useEffect, useRef } from 'react';

type Rule = {
  number: number;
  title: string;
  description: string;
  steps?: string[];
};

const Rules: React.FC = () => {
  const rulesRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    rulesRef.current.forEach((rule: HTMLDivElement | null) => {
      if (rule) {
        rule.classList.add('fade-in-ready');
        observer.observe(rule);
      }
    });

    return () => {
      rulesRef.current.forEach((rule: HTMLDivElement | null) => {
        if (rule) observer.unobserve(rule);
      });
    };
  }, []);

  const rules: Rule[] = [
    {
      number: 1,
      title: 'No plundering trucks from Server 1924',
      description:
        'Do not plunder any supply trucks belonging to players on Server 1924. These trucks are protected, and hitting them causes unnecessary issues within our own server.',
      steps: [
        'Go to your Train Station inside your base.',
        'In the top-left corner, check the box labeled "Filter out trucks from your warzone."',
        'With this enabled, Server 1924 trucks will not appear in your warzone list.',
        'Once this filter is active, you may plunder any truck you see in your train center, because those trucks belong to other servers, not 1924.',
      ],
    },
    {
      number: 2,
      title: 'No plundering secret tasks from Server 1924 members',
      description:
        'Secret tasks are the colorful boxes that appear on the map. These boxes are personal rewards for the player who finds them. Because of this, you may not plunder or steal secret task boxes from anyone on Server 1924.',
      steps: [
        'Go to your Train Station inside your base.',
        'Select a train that does NOT belong to Server 1924.',
        'Click "Plunder."',
        'This will move you into that train\'s server.',
        'Search the map there to plunder secret task boxes safely from that server instead of ours.',
      ],
    },
    {
      number: 3,
      title: 'No attacking bases in NAP15 alliances or around the Capital',
      description:
        'NAP15 is a Non-Aggression Pact shared by the top 15 alliances on the server. All alliances under this pact follow the same rules and cannot attack each other. This agreement also includes any alliance located around the Capital, which is at the center of the map. These alliances are also protected. Because of this, any alliance or player included in NAP15 is strictly off-limits, even if their base appears unshielded or inactive.',
    },
  ];

  return (
    <section id="rules" className="section section-dark">
      <div className="container">
        <h2 className="section-title">Server 1924 Rules</h2>
        <div className="mb-6 rounded-xl border border-primary/40 bg-primary/10 p-6 text-primary-light">
          <p className="font-semibold">
            Presidential mail is not auto-written — read it fully for current rules, event updates, and server goals before
            you head out to plunder or fight.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          {rules.map((rule, index) => (
            <div
              key={index}
              className="flex gap-8 mb-8 bg-bg-card p-8 rounded-xl border border-border transition-all duration-300 hover:border-primary hover:shadow-glow md:flex-row flex-col"
              ref={(el) => {
                if (el) rulesRef.current[index] = el;
              }}
            >
              <span className="font-orbitron text-5xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent shrink-0 w-15 text-center md:w-auto">
                {rule.number}
              </span>
              <div>
                <h3 className="text-2xl mb-2 text-primary-light">{rule.title}</h3>
                <p className="text-text-secondary leading-relaxed mb-4">{rule.description}</p>
                {rule.steps && rule.steps.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-primary-light font-semibold mb-2">
                      {rule.number === 1 ? 'To avoid accidentally plundering friendly trucks:' : 
                       rule.number === 2 ? 'If you want to plunder secret tasks, do so on another server:' : 
                       'How to comply:'}
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-text-secondary">
                      {rule.steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="leading-relaxed pl-2">
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Rules;
